window.ProductDetailPage = function ProductDetailPage({ 
  productSlug, 
  setActiveTab, 
  openQuoteModal, 
  setSelectedProductSlug 
}) {
  const [product, setProduct] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [activeGalleryIndex, setActiveGalleryIndex] = React.useState(0);
  const [downloading, setDownloading] = React.useState(false);
  const [relatedProducts, setRelatedProducts] = React.useState([]);
  const [isLightboxOpen, setIsLightboxOpen] = React.useState(false);

  // Compute gallery images early so it is accessible in state and hooks
  const primaryImg = product ? (product.primary_image || product.image || '/api/storage/banners/hero_railways.png') : '';
  let galleryImages = [];
  if (product) {
    if (product.gallery && Array.isArray(product.gallery) && product.gallery.length > 0) {
      const hasPrimary = product.gallery.some(img => img === primaryImg);
      if (!hasPrimary && primaryImg) {
        galleryImages = [primaryImg, ...product.gallery];
      } else {
        galleryImages = product.gallery;
      }
    } else {
      galleryImages = [primaryImg];
    }
  }

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchProductDossier();

    const handleSync = () => fetchProductDossier();
    window.addEventListener('novatek_content_updated', handleSync);
    window.addEventListener('storage', handleSync);

    return () => {
      window.removeEventListener('novatek_content_updated', handleSync);
      window.removeEventListener('storage', handleSync);
    };
  }, [productSlug]);

  // Keyboard navigation for Lightbox
  React.useEffect(() => {
    if (!isLightboxOpen || galleryImages.length <= 1) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsLightboxOpen(false);
      } else if (e.key === 'ArrowRight') {
        setActiveGalleryIndex((prev) => (prev + 1) % galleryImages.length);
      } else if (e.key === 'ArrowLeft') {
        setActiveGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, galleryImages.length]);

  const fetchProductDossier = async () => {
    try {
      setLoading(true);
      const isNumeric = !isNaN(productSlug) && !isNaN(parseFloat(productSlug));
      const queryParam = isNumeric ? `id=${productSlug}` : `slug=${encodeURIComponent(productSlug)}`;
      const res = await window.apiClient.get(`products.php?${queryParam}`);
      if (res.success && res.data) {
        setProduct(res.data);
        updateSeo(res.data);
      } else {
        setProduct(null);
      }
    } catch (e) {
      console.warn('Product detail fetch error:', e);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  const updateSeo = (prod) => {
    if (window.SEOHelper && prod) {
      window.SEOHelper.updatePageMeta(
        prod.seo_title || `${prod.name || prod.title} | Novatek Custom Test Systems`,
        prod.short_description || prod.shortDesc || prod.description
      );
      window.SEOHelper.injectProductSchema(prod);
      window.SEOHelper.injectBreadcrumbSchema([
        { name: "Home", url: "/?tab=home" },
        { name: "Products", url: "/?tab=products" },
        { name: prod.category_name || prod.category || "Test Systems", url: `/?tab=products` },
        { name: prod.name || prod.title, url: window.location.href }
      ]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center font-mono text-xs text-slate-400">
        Loading engineering dossier from database...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center">
        <h2 className="text-2xl font-bold font-sans text-slate-800">Product Not Found</h2>
        <p className="text-slate-500 mt-2">The requested engineering specification is unavailable.</p>
        <button 
          onClick={() => setActiveTab('products')} 
          className="mt-6 bg-secondary text-white px-6 py-2.5 rounded-lg font-semibold"
        >
          Return to Product Catalogue
        </button>
      </div>
    );
  }



  const handleDownloadDatasheet = (e) => {
    e.preventDefault();
    if (product.datasheet_url || product.datasheetUrl) {
      window.open(window.resolveMediaUrl(product.datasheet_url || product.datasheetUrl), '_blank');
      return;
    }
    setDownloading(true);
    setTimeout(() => {
      setDownloading(false);
      window.print();
    }, 500);
  };

  return (
    <div className="bg-slate-50 min-h-screen py-10 print:py-0 print:bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 print:space-y-6">
        
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-xs font-mono text-slate-500 print:hidden">
          <button onClick={() => setActiveTab('home')} className="hover:text-slate-900">Home</button>
          <span>/</span>
          <button onClick={() => setActiveTab('products')} className="hover:text-slate-900">Products</button>
          <span>/</span>
          <span className="text-secondary font-semibold">{product.category_name || product.category || 'Test System'}</span>
          <span>/</span>
          <span className="text-slate-900 truncate max-w-xs">{product.name || product.title}</span>
        </nav>

        {/* 1. HERO PRODUCT CARD */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-10">
          
          {/* Left Column: Image Gallery (5 Cols) */}
          <div className="lg:col-span-5 space-y-4">
            <div 
              onClick={() => setIsLightboxOpen(true)}
              className="h-72 sm:h-96 rounded-2xl overflow-hidden bg-slate-900 border border-slate-200 relative group cursor-zoom-in"
              title="Click to view full screen"
            >
              <img 
                src={window.resolveMediaUrl(galleryImages[activeGalleryIndex] || primaryImg, 'machine')} 
                alt={product.name || product.title}
                onError={(e) => window.handleImageError(e, 'machine')}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <span className="absolute top-3 left-3 bg-slate-900/85 backdrop-blur-xs text-secondary text-xs font-mono font-bold px-3 py-1 rounded border border-orange-500/30">
                {product.product_code || 'NV-MACHINE'}
              </span>
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                <span className="material-symbols-outlined text-white text-3xl drop-shadow-md">fullscreen</span>
              </div>
            </div>

            {/* Thumbnail Row */}
            {galleryImages.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      if (activeGalleryIndex === idx) {
                        setIsLightboxOpen(true);
                      } else {
                        setActiveGalleryIndex(idx);
                      }
                    }}
                    onDoubleClick={() => {
                      setActiveGalleryIndex(idx);
                      setIsLightboxOpen(true);
                    }}
                    className={`h-16 w-20 rounded-xl overflow-hidden border-2 shrink-0 transition-all ${
                      activeGalleryIndex === idx ? 'border-secondary shadow-md scale-105' : 'border-slate-200 opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={window.resolveMediaUrl(img, 'machine')} 
                      alt={`Thumbnail ${idx}`} 
                      onError={(e) => window.handleImageError(e, 'machine')}
                      className="w-full h-full object-cover" 
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Download Datasheet Banner */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <span className="font-bold font-sans text-sm text-slate-900 block">Engineering Datasheet</span>
                <span className="text-xs text-slate-500 font-sans">Full technical specs & dimension prints</span>
              </div>
              <button 
                onClick={handleDownloadDatasheet}
                disabled={downloading}
                className="bg-slate-900 hover:bg-secondary text-white font-mono text-xs font-bold px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition-colors shrink-0"
              >
                <span className="material-symbols-outlined text-sm">download</span>
                <span>{downloading ? 'Preparing...' : 'PDF Dossier'}</span>
              </button>
            </div>
          </div>

          {/* Right Column: Key Overview & Scope (7 Cols) */}
          <div className="lg:col-span-7 space-y-6 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs font-bold text-secondary uppercase tracking-widest bg-orange-50 px-3 py-1 rounded">
                  {product.category_name || product.category || 'Test System'}
                </span>
                {product.badge && (
                  <span className="font-mono text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded">
                    {product.badge}
                  </span>
                )}
              </div>

              <h1 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
                {product.name || product.title}
              </h1>

              <p className="font-sans text-slate-600 text-sm sm:text-base leading-relaxed">
                {product.full_description || product.description || product.short_description || product.shortDesc}
              </p>

              {/* Purpose & Applications List */}
              {product.applications && product.applications.length > 0 && (
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 space-y-3">
                  <h3 className="font-sans text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <span className="material-symbols-outlined text-secondary text-base">check_circle</span>
                    <span>Target Applications & Test Scope:</span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-sans text-slate-700">
                    {product.applications.map((app, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="text-secondary font-bold font-mono">▸</span>
                        <span>{app}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* CTAs */}
            <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row gap-4 print:hidden">
              <button
                onClick={openQuoteModal}
                className="flex-1 bg-secondary hover:bg-orange-600 text-white font-mono text-xs sm:text-sm font-bold uppercase py-3.5 px-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-lg">engineering</span>
                <span>Request Engineering Consultation</span>
              </button>

              <button
                onClick={openQuoteModal}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-mono text-xs sm:text-sm font-bold uppercase py-3.5 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-lg">request_quote</span>
                <span>Request Quotation</span>
              </button>
            </div>

          </div>

        </div>

        {/* 2. TESTING PARAMETERS MEASURED */}
        {product.testingParameters && product.testingParameters.length > 0 && (
          <section className="space-y-6">
            <div>
              <span className="font-mono text-xs font-bold text-secondary tracking-widest uppercase block">
                Sensor Transduction & Calibration
              </span>
              <h2 className="font-sans text-2xl font-bold text-slate-900 mt-1">
                Testing Parameters Measured & Logged
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {product.testingParameters.map((param, idx) => (
                <div key={idx} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-start gap-3.5">
                  <div className="h-10 w-10 rounded-xl bg-orange-50 text-secondary flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-xl">{param.icon || 'speed'}</span>
                  </div>
                  <div>
                    <h4 className="font-sans text-sm font-bold text-slate-900">{param.name}</h4>
                    <span className="font-mono text-xs text-secondary font-semibold block mt-0.5">{param.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 3. OPERATIONAL WORKFLOW (HOW THE SYSTEM WORKS) */}
        {product.howItWorks && product.howItWorks.length > 0 && (
          <section className="bg-slate-900 text-white rounded-3xl p-8 sm:p-12 space-y-8">
            <div>
              <span className="font-mono text-xs font-bold text-secondary tracking-widest uppercase block">
                Operational Architecture
              </span>
              <h2 className="font-sans text-2xl sm:text-3xl font-bold text-white mt-1">
                How The Test System Operates
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {product.howItWorks.map((step, idx) => (
                <div key={idx} className="bg-slate-800/80 rounded-2xl p-6 border border-slate-700 space-y-3 relative">
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-xl font-bold text-secondary">
                      {step.step || (idx + 1).toString().padStart(2, '0')}
                    </span>
                    <span className="h-2 w-2 rounded-full bg-orange-500"></span>
                  </div>
                  <h3 className="font-sans text-base font-bold text-white">{step.title}</h3>
                  <p className="font-sans text-xs text-slate-300 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 4. TECHNICAL SPECIFICATIONS TABLE */}
        {product.specs && Object.keys(product.specs).length > 0 && (
          <section className="space-y-6">
            <div>
              <span className="font-mono text-xs font-bold text-secondary tracking-widest uppercase block">
                Engineering Blueprint
              </span>
              <h2 className="font-sans text-2xl font-bold text-slate-900 mt-1">
                Technical Specifications & Capabilities
              </h2>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <tbody className="divide-y divide-slate-200 bg-white">
                  {Object.entries(product.specs).map(([key, val], idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="px-6 py-3.5 font-sans font-bold text-slate-900 text-xs sm:text-sm">{key}</td>
                      <td className="px-6 py-3.5 font-sans text-slate-700 text-xs sm:text-sm">{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* 5. INSTRUMENTATION & STANDARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Sensors */}
          {product.sensorsInstrumentation && product.sensorsInstrumentation.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-4">
              <div className="flex items-center gap-2 text-slate-900">
                <span className="material-symbols-outlined text-secondary text-2xl">sensors</span>
                <h3 className="font-sans text-lg font-bold">Instrumentation & Sensors</h3>
              </div>
              <ul className="space-y-2.5 text-xs sm:text-sm font-sans text-slate-600">
                {product.sensorsInstrumentation.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="text-secondary font-bold font-mono">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Automation & Standards */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-4">
            <div className="flex items-center gap-2 text-slate-900">
              <span className="material-symbols-outlined text-secondary text-2xl">verified</span>
              <h3 className="font-sans text-lg font-bold">Automation, Software & Standards</h3>
            </div>
            
            {product.automationSoftware && (
              <ul className="space-y-2 text-xs sm:text-sm font-sans text-slate-600">
                {product.automationSoftware.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="text-secondary font-bold font-mono">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}

            {product.standardsCompliance && product.standardsCompliance.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <span className="text-xs font-mono font-bold text-slate-500 block mb-2">Standards Compliance:</span>
                <div className="flex flex-wrap gap-2">
                  {product.standardsCompliance.map((std, i) => (
                    <span key={i} className="bg-orange-50 border border-orange-200 text-secondary text-xs font-mono font-bold px-2.5 py-1 rounded">
                      {std}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Lightbox Modal */}
        {isLightboxOpen && (
          <div 
            className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-fadeIn select-none"
            onClick={() => setIsLightboxOpen(false)}
          >
            {/* Close Button */}
            <button 
              onClick={() => setIsLightboxOpen(false)}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 rounded-full transition-all border border-white/10 cursor-pointer shadow-lg z-50 animate-fadeIn"
            >
              <span className="material-symbols-outlined text-2xl sm:text-3xl block">close</span>
            </button>
            
            {/* Navigation: Previous Button */}
            {galleryImages.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
                }}
                className="absolute left-4 sm:left-8 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-3 sm:p-4 rounded-full transition-all border border-white/10 shadow-lg cursor-pointer z-50"
                title="Previous Image"
              >
                <span className="material-symbols-outlined text-2xl sm:text-3xl block">chevron_left</span>
              </button>
            )}

            {/* Navigation: Next Button */}
            {galleryImages.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveGalleryIndex((prev) => (prev + 1) % galleryImages.length);
                }}
                className="absolute right-4 sm:right-8 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-3 sm:p-4 rounded-full transition-all border border-white/10 shadow-lg cursor-pointer z-50"
                title="Next Image"
              >
                <span className="material-symbols-outlined text-2xl sm:text-3xl block">chevron_right</span>
              </button>
            )}

            {/* Full Screen Image Container */}
            <div 
              className="relative max-w-5xl max-h-[70vh] w-full flex flex-col items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={window.resolveMediaUrl(galleryImages[activeGalleryIndex] || primaryImg, 'machine')} 
                alt={product.name || product.title}
                onError={(e) => window.handleImageError(e, 'machine')}
                className="max-w-full max-h-[70vh] object-contain rounded-2xl border border-white/10 shadow-2xl"
              />
            </div>

            {/* Bottom Row: Info Counter & Thumbnails */}
            <div 
              className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 w-full px-4 max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Image Counter */}
              <span className="bg-slate-900/85 text-white/90 font-mono text-xs px-4 py-1.5 rounded-full border border-white/10 backdrop-blur-xs select-none shadow-lg">
                {activeGalleryIndex + 1} / {galleryImages.length}
              </span>

              {/* Thumbnails list inside lightbox */}
              {galleryImages.length > 1 && (
                <div className="flex gap-2.5 overflow-x-auto max-w-full pb-2 custom-scrollbar justify-center">
                  {galleryImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveGalleryIndex(idx)}
                      className={`h-12 w-16 rounded-lg overflow-hidden border-2 shrink-0 transition-all ${
                        activeGalleryIndex === idx ? 'border-secondary shadow-md scale-105' : 'border-white/10 opacity-50 hover:opacity-100'
                      }`}
                    >
                      <img 
                        src={window.resolveMediaUrl(img, 'machine')} 
                        alt={`Lightbox Thumbnail ${idx}`} 
                        onError={(e) => window.handleImageError(e, 'machine')}
                        className="w-full h-full object-cover" 
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}

      </div>
    </div>
  );
};
